import UserControlPanel from "@common-components/user-control-panel/user-control-panel";
import ThemeAsset from "@common-components/theme/theme-asset";

import { appConfig } from "@common-config/app.config";
import { namespace } from "@common-config/constants";
import { isCurrentUserMemberOrOwner } from "@common-reducers/BoardsSelectors";
import { workspacesSelectByQueryOnlyLength } from "@common-reducers/DBServiceThunks";
import { useAppDispatch, useAppSelector } from "@common-reducers/hooks/store.hook";
import { isUserWorkSpaceOwner, selectUserObject } from "@common-reducers/UserSelectors";
import { logoutWrapperThunk } from "@common-reducers/UserThunks";
import { User } from "@common/models/user";
import { BackupOutlined, BarChart, Code, ExitToAppOutlined, FactCheckOutlined, GppGoodOutlined, PersonOutlineOutlined, Receipt, ShoppingCart } from "@mui/icons-material";
import { Divider } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";

import { SidebarLinkButton } from "./sidebar-link-button";
import { canSeeCreditsUsage } from "./sidebar-visibility";
import { SidebarLogo } from "./sidebar-logo"; // Import logo component
import { cn } from "@/lib/utils";

// The enum now lives in a dependency-free module; re-export it here so existing
// `import { SideBarItem } from "@common-components/sidebar/sidebar"` call sites
// keep working unchanged.
export { SideBarItem } from "./sidebar-items";
import { SideBarItem } from "./sidebar-items";

// Map routes to sidebar items
const ROUTE_TO_SIDEBAR_ITEM: Record<string, SideBarItem> = {
  '/account': SideBarItem.AccountSettings,
  '/account-language': SideBarItem.Language,
  '/account-notification': SideBarItem.Notifications,
  '/account-security': SideBarItem.Security,
  '/account-backup': SideBarItem.Backup,
  '/account-workspace': SideBarItem.Workspace,
  '/admin-workspace': SideBarItem.AdminWorkspace,
  '/user-management': SideBarItem.UserManagement,
  '/blueticks-api': SideBarItem.AdminAPI,
  '/admin-billing': SideBarItem.AdminBilling,
  '/account-subscription': SideBarItem.Subscriptions,
  '/account-invoices': SideBarItem.Invoices,
  '/devices': SideBarItem.Devices,
  '/calendar': SideBarItem.Calendar,
  '/campaigns': SideBarItem.Campaigns,
  '/drips': SideBarItem.Drips,
  '/tasks': SideBarItem.Tasks,
  '/launch-campaign': SideBarItem.LaunchCampaign,
  '/manage-campaigns': SideBarItem.LaunchCampaign,
  '/manage-audiences': SideBarItem.LaunchCampaign,
  '/manage-templates': SideBarItem.LaunchCampaign,
  '/audience': SideBarItem.LaunchCampaign,
  '/campaigns-management': SideBarItem.CampaignsManagement,
  '/bot-list-view': SideBarItem.Agents,
  '/credits-usage': SideBarItem.CreditsUsage,
  '/whatsapp-numbers': SideBarItem.WhatsAppNumbers,
  '/support': SideBarItem.Support,
  '/bt-team-inbox': SideBarItem.TeamInbox,
};

export interface SideBarProps {
  readOnly?: boolean;
}

export interface SideBarState {
  user?: User | null | undefined;
}

const SideBar = ({ readOnly }: SideBarProps) => {
  const { t } = useTranslation(namespace);
  const dispatch = useAppDispatch()
  const accessToken = useAppSelector((state) => state.UserReducer.accessToken);
  const isMemberOrOwner = useAppSelector(isCurrentUserMemberOrOwner);
  const isWorkSpaceOwner = useAppSelector(isUserWorkSpaceOwner);
  const user = useAppSelector(selectUserObject);
  const location = useLocation();
  const workspacesCount = useAppSelector((state) =>
    workspacesSelectByQueryOnlyLength(state.DBWorkspaceReducer, { deleted: { $ne: true } })
  );
  // Get active item based on current route - improved to handle path variations
  const getActiveSidebarItem = () => {
    const path = location.pathname;
    
    // Normalize the path - remove trailing slashes and handle both with/without leading slash
    const normalizedPath = path.endsWith('/') ? path.slice(0, -1) : path;
    const pathWithoutLeadingSlash = normalizedPath.startsWith('/') ? normalizedPath.substring(1) : normalizedPath;
    
    // Try exact match first
    if (ROUTE_TO_SIDEBAR_ITEM[normalizedPath]) {
      return ROUTE_TO_SIDEBAR_ITEM[normalizedPath];
    }
    
    // Then try without leading slash
    if (ROUTE_TO_SIDEBAR_ITEM[`/${pathWithoutLeadingSlash}`]) {
      return ROUTE_TO_SIDEBAR_ITEM[`/${pathWithoutLeadingSlash}`];
    }
    
    // Check if the path ends with any of our known routes
    for (const route in ROUTE_TO_SIDEBAR_ITEM) {
      if (normalizedPath.endsWith(route)) {
        return ROUTE_TO_SIDEBAR_ITEM[route];
      }
    }
    
    return undefined;
  };

  const active = getActiveSidebarItem();

  return (
    <div className={cn(
      "relative min-w-[300px] flex flex-col flex-auto",
      "bg-gradient-to-b from-blue-50 to-white dark:bg-gradient-to-b dark:from-slate-800 dark:to-slate-900",
      "border-r border-slate-200 dark:border-slate-700",
      "shadow-lg transition-colors duration-300"
    )}>
      {/* Header with BlueTicks logo */}
      <div className={cn(
        "flex items-center px-4 gap-6 h-20 box-border",
        "bg-transparent"
      )}>
        <SidebarLogo />
      </div>
      
      {readOnly &&
        <span className="text-sm p-2.5 text-slate-600 dark:text-slate-400">
          Support mode <br />
          Name:{user?.firstName} {user?.lastName}
          <br />
          email: {user?.email}
        </span>
      }
      
      <div className="flex flex-col">


        <SidebarLinkButton
          to="/account"
          icon={PersonOutlineOutlined}
          titleKey="sideBar.personalInformation"
          active={SideBarItem.AccountSettings}
          currentActive={active}
          analyticsAction="switch_to_personal_information"
          preserveQueryParams={true}
        />
        
        {!readOnly &&
          <SidebarLinkButton
            to="/account-security"
            icon={GppGoodOutlined}
            titleKey="sideBar.security"
            active={SideBarItem.Security}
            currentActive={active}
            analyticsAction="switch_to_account_security"
            preserveQueryParams={true}
          />
        }

        {/* Message-backup settings are ADMIN/SUPPORT-only for now (not exposed
            to regular users in production). The local vault + deleted-message
            recovery still run for everyone; this only hides the config page. */}
        {!readOnly && (user?.role === "support" || user?.role === "admin") &&
          <SidebarLinkButton
            to="/account-backup"
            icon={BackupOutlined}
            titleKey="sideBar.backup"
            active={SideBarItem.Backup}
            currentActive={active}
            analyticsAction="switch_to_account_backup"
            preserveQueryParams={true}
          />
        }

        {workspacesCount > 1 && <SidebarLinkButton
          to="/account-workspace"
          icon={FactCheckOutlined}
          titleKey="sideBar.workspaces"
          active={SideBarItem.Workspace}
          currentActive={active}
          analyticsAction="switch_to_account_workspace"
          preserveQueryParams={true}
        />}

        {isMemberOrOwner && <SidebarLinkButton
          to="/user-management"
          icon={FactCheckOutlined}
          titleKey="sideBar.userManagement"
          active={SideBarItem.UserManagement}
          currentActive={active}
          analyticsAction="switch_to_user_management"
          preserveQueryParams={true}
        />}
        
        {appConfig.asset_key === 'blueticks' && <SidebarLinkButton
          to="/blueticks-api"
          icon={Code}
          titleKey="sideBar.api"
          active={SideBarItem.AdminAPI}
          currentActive={active}
          analyticsAction="switch_to_admin_api"
          testId="admin-api-view-button"
          preserveQueryParams={true}
        />}
        
        
        {(isWorkSpaceOwner || user?.role === "support" || user?.role === "admin") &&
          <SidebarLinkButton
            to="/admin-billing"
            icon={Receipt}
            titleKey="sideBar.adminBilling"
            active={SideBarItem.AdminBilling}
            currentActive={active}
            analyticsAction="switch_to_admin_billing"
            testId="admin-billing-view-button"
            preserveQueryParams={true}
          />
        }
        
        {isWorkSpaceOwner && !readOnly && <SidebarLinkButton
          to="/account-subscription"
          icon={ShoppingCart}
          titleKey="sideBar.buySubscription"
          active={SideBarItem.Subscriptions}
          currentActive={active}
          analyticsAction="switch_to_account_subscription"
          testId="buy-subscription-view-button"
          preserveQueryParams={true}
        />}
        
        {/* Credits Usage Link */}
        {canSeeCreditsUsage({ isWorkSpaceOwner, isMemberOrOwner, readOnly }) && <SidebarLinkButton
          to="/credits-usage"
          icon={BarChart}
          titleKey="sideBar.creditsUsage"
          active={SideBarItem.CreditsUsage}
          currentActive={active}
          analyticsAction="switch_to_credits_usage"
          testId="credits-usage-view-button"
          preserveQueryParams={true}
        />}
        
        {!readOnly &&
          <div 
            className={cn(
              "flex items-center gap-3 px-4 py-3 mx-2 rounded-lg cursor-pointer",
              "text-sm font-medium transition-all duration-300",
              "text-slate-600 dark:text-slate-300",
              "hover:bg-red-50 dark:hover:bg-red-900/20",
              "hover:text-red-600 dark:hover:text-red-400",
              "hover:scale-[1.02] group"
            )}
            onClick={() => {
              dispatch(logoutWrapperThunk({ accessToken: accessToken ?? null }))
            }}
          >
            <ExitToAppOutlined className="!w-5 !h-5 text-slate-500 dark:text-slate-400 group-hover:text-red-600 dark:group-hover:text-red-400 transition-colors duration-300" />
            <span className="whitespace-nowrap">{t('sideBar.logout')}</span>
          </div>
        }
      </div >

      <div className="mt-auto flex flex-col py-2" />
      
      <UserControlPanel isSideMenuOpen={true} readOnly={readOnly} />
    </div >
  );
};

export default SideBar;
